import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { useMasterData } from '../contexts/MasterDataContext';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Loader2, CheckCircle, IndianRupee } from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';

const PAYMENT_MODES = ['Cash', 'UPI', 'Online', 'Wallet'];
const BOOKING_SOURCES = ['Calling', 'Khelomore', 'District', 'Hudle', 'WhatsApp', 'App'];

const TIME_SLOTS = [];
for (let hour = 6; hour <= 26; hour++) {
  for (let minute = 0; minute < 60; minute += 30) {
    const totalMinutes = hour * 60 + minute;
    const displayHour = Math.floor(totalMinutes / 60);
    const displayMinute = totalMinutes % 60;
    const finalHour = displayHour > 23 ? displayHour - 24 : displayHour;
    const ampm = finalHour < 12 ? 'AM' : 'PM';
    const displayHour12 = finalHour === 0 ? 12 : finalHour > 12 ? finalHour - 12 : finalHour;
    TIME_SLOTS.push(`${String(displayHour12).padStart(2, '0')}:${String(displayMinute).padStart(2, '0')} ${ampm}`);
    if (displayHour >= 26) break;
  }
}

// Helper function to get end time 60 minutes after start time
const getEndTimeAfter60Min = (startTime) => {
  const startIndex = TIME_SLOTS.indexOf(startTime);
  if (startIndex === -1) return '';
  // 60 minutes = 2 slots (since each slot is 30 min)
  const endIndex = startIndex + 2;
  if (endIndex < TIME_SLOTS.length) {
    return TIME_SLOTS[endIndex];
  }
  return TIME_SLOTS[TIME_SLOTS.length - 1];
};

// Get today's date in YYYY-MM-DD format (India time, independent of browser/server timezone)
const getTodayDate = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

export default function NewBookingPage() {
  const { getVenueNames, getGamesForVenue, getCourtsForVenueGame } = useMasterData();
  const VENUES = getVenueNames();
  
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [players, setPlayers] = useState([]);
  const [playerSearch, setPlayerSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [showAvailableSlots, setShowAvailableSlots] = useState(false);
  const skipSearchRef = useRef(false);
  const playerNameRef = useRef(null);

  const [formData, setFormData] = useState({
    venue: '',
    game: '',
    court: '',
    date: getTodayDate(),
    start_time: '',
    end_time: '',
    player_id: '',
    player_name: '',
    player_mobile: '',
    amount: '',
    advance_payment: '',
    payment_mode: 'UPI',
    booking_source: 'Calling',
    remarks: '',
  });

  // State for available time slots (filtered)
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [blockedTimeSlots, setBlockedTimeSlots] = useState([]);
  const [allSlotData, setAllSlotData] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  // Calculate pending amount
  const totalAmount = parseFloat(formData.amount) || 0;
  const advancePayment = parseFloat(formData.advance_payment) || 0;
  const pendingAmount = Math.max(0, totalAmount - advancePayment);

  useEffect(() => {
    fetchPlayers();
  }, []);

  // Fetch available time slots when venue, game, court, or date changes
  useEffect(() => {
    const fetchAvailableSlots = async () => {
      if (!formData.venue || !formData.game || !formData.court || !formData.date) {
        setAvailableTimeSlots(TIME_SLOTS); // Show all if not fully selected
        setBlockedTimeSlots([]);
        setAllSlotData([]);
        return;
      }

      setLoadingSlots(true);
      try {
        const response = await axios.get(`${API}/slot-finder`, {
          params: {
            venue: formData.venue,
            game: formData.game,
            court: formData.court,
            date: formData.date
          }
        });
        
        // Store all slot data for rendering
        setAllSlotData(response.data.slots || []);
        
        // Filter to only available slots (not booked and not blocked)
        const available = response.data.slots
          .filter(slot => slot.available && !slot.is_blocked)
          .map(slot => slot.start_time);
        
        // Get blocked slots (for display in red)
        const blocked = response.data.slots
          .filter(slot => slot.is_blocked)
          .map(slot => slot.start_time);
        
        setAvailableTimeSlots(available);
        setBlockedTimeSlots(blocked);
        
        // Clear start/end time if currently selected time is no longer available or is blocked
        if (formData.start_time && (!available.includes(formData.start_time) || blocked.includes(formData.start_time))) {
          setFormData(prev => ({ ...prev, start_time: '', end_time: '' }));
        }
      } catch (error) {
        console.error('Failed to fetch available slots:', error);
        setAvailableTimeSlots(TIME_SLOTS); // Fallback to all slots
        setBlockedTimeSlots([]);
        setAllSlotData([]);
      } finally {
        setLoadingSlots(false);
      }
    };

    fetchAvailableSlots();
  }, [formData.venue, formData.game, formData.court, formData.date]);

  useEffect(() => {
    // Skip one run right after a contact is selected (selecting sets playerSearch
    // to the chosen name, which would otherwise re-open the dropdown).
    if (skipSearchRef.current) {
      skipSearchRef.current = false;
      return;
    }
    if (playerSearch && playerSearch.length >= 2) {
      const searchLower = playerSearch.toLowerCase();
      const filtered = players.filter(
        (p) =>
          (p.name && p.name.toLowerCase().includes(searchLower)) ||
          (p.mobile && p.mobile.includes(playerSearch))
      ).slice(0, 10); // Limit to 10 results
      setSearchResults(filtered);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [playerSearch, players]);

  const fetchPlayers = async () => {
    try {
      // Fetch from contacts API which has aggregated player data
      const response = await axios.get(`${API}/contacts`);
      // Endpoint returns { contacts: [...] }
      const list = Array.isArray(response.data) ? response.data : (response.data.contacts || []);
      const contacts = list.map(contact => ({
        id: contact.player_mobile || contact.player_name,
        name: contact.player_name,
        mobile: contact.player_mobile || 'N/A'
      }));
      setPlayers(contacts);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
      // Fallback to players API if contacts fails
      try {
        const playersResponse = await axios.get(`${API}/players`);
        setPlayers(playersResponse.data);
      } catch (e) {
        console.error('Failed to fetch players:', e);
      }
    }
  };

  const selectPlayer = (player) => {
    setFormData({
      ...formData,
      player_id: player.id,
      player_name: player.name,
      player_mobile: player.mobile,
    });
    skipSearchRef.current = true;
    setPlayerSearch(player.name);
    setShowSearchResults(false);
    setSearchResults([]);
    // Auto-advance focus to the next field
    setTimeout(() => playerNameRef.current?.focus(), 0);
  };

  const checkAvailability = async () => {
    if (!formData.venue || !formData.game || !formData.court || !formData.date || !formData.start_time || !formData.end_time) {
      return;
    }

    setCheckingAvailability(true);
    try {
      const response = await axios.post(`${API}/bookings/check-availability`, {
        venue: formData.venue,
        game: formData.game,
        court: formData.court,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
      });

      if (!response.data.available) {
        toast.error('This time slot is already booked! Please select a different time or court.');
      }
    } catch (error) {
      console.error('Failed to check availability:', error);
      toast.error('Failed to check availability');
    } finally {
      setCheckingAvailability(false);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!formData.venue || !formData.game || !formData.court || !formData.date) {
      return;
    }

    try {
      // Use slot-finder API which includes both booked and blocked slots
      const response = await axios.get(`${API}/slot-finder`, {
        params: {
          venue: formData.venue,
          game: formData.game,
          court: formData.court,
          date: formData.date,
        },
      });

      // Get available slots (not booked and not blocked)
      const available = response.data.slots
        .filter(slot => slot.available && !slot.is_blocked)
        .map(slot => slot.start_time);
      
      // Get blocked slots
      const blocked = response.data.slots
        .filter(slot => slot.is_blocked)
        .map(slot => slot.start_time);
      
      // Store all slot data for tooltip info
      setAllSlotData(response.data.slots || []);
      setAvailableSlots(available);
      setBlockedTimeSlots(blocked);
      setShowAvailableSlots(true);
    } catch (error) {
      console.error('Failed to fetch available slots:', error);
      // Fallback to old method if slot-finder fails
      try {
        const response = await axios.get(`${API}/bookings`, {
          params: {
            venue: formData.venue,
            game: formData.game,
            date: formData.date,
          },
        });

        const bookedSlots = response.data
          .filter((b) => b.court === formData.court)
          .map((b) => ({
            start: b.start_time,
            end: b.end_time,
          }));

        const available = TIME_SLOTS.filter((slot, index) => {
          if (index === TIME_SLOTS.length - 1) return false;
          const nextSlot = TIME_SLOTS[index + 1];
          
          const isBooked = bookedSlots.some((booked) => {
            return (
              (slot >= booked.start && slot < booked.end) ||
              (nextSlot > booked.start && nextSlot <= booked.end) ||
              (slot <= booked.start && nextSlot >= booked.end)
            );
          });

          return !isBooked;
        });

        setAvailableSlots(available);
        setBlockedTimeSlots([]);
        setShowAvailableSlots(true);
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
      }
    }
  };

  useEffect(() => {
    if (formData.venue && formData.game && formData.court && formData.date) {
      fetchAvailableSlots();
    } else {
      setShowAvailableSlots(false);
    }
  }, [formData.venue, formData.game, formData.court, formData.date]);

  useEffect(() => {
    if (formData.venue && formData.game && formData.court && formData.date && formData.start_time && formData.end_time) {
      const timer = setTimeout(() => {
        checkAvailability();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [formData.venue, formData.game, formData.court, formData.date, formData.start_time, formData.end_time]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Final availability check before creating booking
      const availabilityCheck = await axios.post(`${API}/bookings/check-availability`, {
        venue: formData.venue,
        game: formData.game,
        court: formData.court,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
      });

      if (!availabilityCheck.data.available) {
        toast.error('This time slot is already booked! Please select a different time or court.');
        setLoading(false);
        return;
      }

      let playerId = formData.player_id;

      // Only create player if mobile number is provided
      if (!playerId && formData.player_name && formData.player_mobile) {
        const playerResponse = await axios.post(`${API}/players`, {
          name: formData.player_name,
          mobile: formData.player_mobile,
        });
        playerId = playerResponse.data.id;
      }

      // Calculate pending amount for remarks
      const advance = parseFloat(formData.advance_payment) || 0;
      const total = parseFloat(formData.amount) || 0;
      const pending = Math.max(0, total - advance);
      
      // Add payment info to remarks if advance payment exists
      let remarks = formData.remarks || '';
      if (advance > 0) {
        const paymentNote = `[Advance: ₹${advance}, Pending: ₹${pending}]`;
        remarks = remarks ? `${paymentNote} ${remarks}` : paymentNote;
      }

      await axios.post(`${API}/bookings`, {
        ...formData,
        player_id: playerId || 'walk-in',
        player_mobile: formData.player_mobile || 'N/A',
        amount: total,
        advance_payment: advance,
        pending_amount: pending,
        remarks: remarks,
      });

      toast.success('Booking created successfully!');
      navigate('/office/bookings');
    } catch (error) {
      console.error('Failed to create booking:', error);
      const detail = error.response?.data?.detail;
      // Structured overlap response: surface the conflicting booking detail (who/game/platform/ID/bulk)
      if (detail && typeof detail === 'object' && detail.error === 'overlapping_booking' && detail.conflict) {
        const c = detail.conflict;
        const created = (c.created_at || '').replace('T', ' ').split('.')[0];
        const lines = [
          `Booked by: ${c.player_name} (${c.player_mobile})`,
          `Game: ${c.game} · Court: ${c.court}`,
          `Existing slot: ${c.start_time} – ${c.end_time}`,
          `Platform: ${c.booking_source} · ID: ${c.booking_id}`,
          c.is_bulk ? `⚠ BULK BOOKING (${Math.round((c.duration_minutes||0)/6)/10}h)` : null,
          created ? `Booked at: ${created}` : null,
        ].filter(Boolean).join('\n');
        toast.error('Overlapping booking — slot already taken', { description: lines, duration: 14000 });
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed to create booking');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="new-booking-page">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/office/bookings')}
            className="hover:bg-slate-100"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
              New Booking
            </h1>
            <p className="text-slate-600 mt-1">Create a new player booking</p>
          </div>
        </div>

        <Card className="p-6 border-slate-200">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="venue" className="text-slate-700 font-medium">
                  Venue <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.venue}
                  onValueChange={(value) => setFormData({ ...formData, venue: value })}
                  required
                >
                  <SelectTrigger data-testid="venue-select">
                    <SelectValue placeholder="Select venue" />
                  </SelectTrigger>
                  <SelectContent>
                    {VENUES.map((venue) => (
                      <SelectItem key={venue} value={venue}>
                        {venue}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="game" className="text-slate-700 font-medium">
                  Game <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.game}
                  onValueChange={(value) => setFormData({ ...formData, game: value, court: '' })}
                  required
                  disabled={!formData.venue}
                >
                  <SelectTrigger data-testid="game-select">
                    <SelectValue placeholder={formData.venue ? "Select game" : "Select venue first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {getGamesForVenue(formData.venue).map((game) => (
                      <SelectItem key={game} value={game}>
                        {game}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="court" className="text-slate-700 font-medium">
                  Court <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.court}
                  onValueChange={(value) => setFormData({ ...formData, court: value })}
                  required
                  disabled={!formData.game}
                >
                  <SelectTrigger data-testid="court-select">
                    <SelectValue placeholder={formData.game ? "Select court" : "Select game first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {formData.venue && formData.game && getCourtsForVenueGame(formData.venue, formData.game).map((court) => (
                      <SelectItem key={court} value={court}>
                        {court}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formData.venue && formData.game && (
                  <p className="text-xs text-slate-500">
                    {getCourtsForVenueGame(formData.venue, formData.game).length} court(s) available for {formData.game} at {formData.venue}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="date" className="text-slate-700 font-medium">
                  Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  data-testid="date-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="start_time" className="text-slate-700 font-medium">
                  Start Time <span className="text-red-500">*</span>
                  {loadingSlots && <span className="ml-2 text-xs text-slate-400">(Loading...)</span>}
                </Label>
                <Select
                  value={formData.start_time}
                  onValueChange={(value) => {
                    // Prevent selection of blocked slots
                    if (blockedTimeSlots.includes(value)) {
                      toast.error('This slot is blocked and cannot be selected');
                      return;
                    }
                    const autoEndTime = getEndTimeAfter60Min(value);
                    setFormData({ ...formData, start_time: value, end_time: autoEndTime });
                  }}
                  required
                  disabled={!formData.venue || !formData.game || !formData.court || loadingSlots}
                >
                  <SelectTrigger data-testid="start-time-select">
                    <SelectValue placeholder={
                      !formData.venue || !formData.game || !formData.court 
                        ? "Select venue, game & court first" 
                        : availableTimeSlots.length === 0 && blockedTimeSlots.length === 0
                        ? "No available slots" 
                        : "Select start time"
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Available slots in green */}
                    {availableTimeSlots.map((time) => (
                      <SelectItem 
                        key={time} 
                        value={time}
                        className="text-green-700 font-medium"
                      >
                        {time} ✓
                      </SelectItem>
                    ))}
                    {/* Blocked slots in red (disabled visually) */}
                    {blockedTimeSlots.map((time) => {
                      const slotData = allSlotData.find(s => s.start_time === time);
                      return (
                        <SelectItem 
                          key={`blocked-${time}`} 
                          value={time}
                          disabled
                          className="text-red-600 bg-red-50 cursor-not-allowed"
                        >
                          {time} - BLOCKED {slotData?.block_info?.reason ? `(${slotData.block_info.reason})` : ''}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {formData.venue && formData.game && formData.court && availableTimeSlots.length > 0 && (
                  <p className="text-xs text-green-600">
                    {availableTimeSlots.length} slot(s) available
                    {blockedTimeSlots.length > 0 && (
                      <span className="text-red-500 ml-2">• {blockedTimeSlots.length} blocked</span>
                    )}
                  </p>
                )}
                {formData.venue && formData.game && formData.court && availableTimeSlots.length === 0 && !loadingSlots && (
                  <p className="text-xs text-red-500">
                    No available slots for this date. All slots are booked or blocked.
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_time" className="text-slate-700 font-medium">
                  End Time <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.end_time}
                  onValueChange={(value) => setFormData({ ...formData, end_time: value })}
                  required
                  disabled={!formData.start_time}
                >
                  <SelectTrigger data-testid="end-time-select">
                    <SelectValue placeholder={formData.start_time ? "Select end time" : "Select start time first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.filter((time, idx) => idx > TIME_SLOTS.indexOf(formData.start_time)).map((time) => (
                      <SelectItem key={time} value={time}>
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {showAvailableSlots && (availableSlots.length > 0 || blockedTimeSlots.length > 0) && (
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Time Slots for {formData.venue} - {formData.game} - {formData.court} on {formData.date}
                </h4>
                <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-8 gap-2 max-h-48 overflow-y-auto">
                  {/* Available slots in green */}
                  {availableSlots.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      onClick={() => {
                        const slotIndex = TIME_SLOTS.indexOf(slot);
                        const nextSlot = TIME_SLOTS[slotIndex + 1];
                        setFormData({ ...formData, start_time: slot, end_time: nextSlot });
                      }}
                      className="px-3 py-2 text-sm bg-white border border-green-300 rounded-md hover:bg-green-100 hover:border-green-400 transition-colors text-green-800 font-medium"
                      data-testid={`available-slot-${slot}`}
                    >
                      {slot}
                    </button>
                  ))}
                  {/* Blocked slots in red - not clickable with tooltip */}
                  {blockedTimeSlots.map((slot) => {
                    const slotData = allSlotData.find(s => s.start_time === slot);
                    const blockReason = slotData?.block_info?.reason || 'Unknown';
                    const blockNotes = slotData?.block_info?.notes || '';
                    return (
                      <TooltipProvider key={`blocked-${slot}`}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              className="px-3 py-2 text-sm bg-red-100 border border-red-300 rounded-md text-red-700 font-medium cursor-not-allowed"
                              data-testid={`blocked-slot-${slot}`}
                            >
                              {slot}
                              <span className="block text-xs text-red-500">BLOCKED</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="bg-red-900 text-white border-red-800 max-w-xs">
                            <div className="text-sm">
                              <p className="font-semibold">🚫 Blocked: {blockReason}</p>
                              {blockNotes && <p className="mt-1 text-red-200">{blockNotes}</p>}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    );
                  })}
                </div>
                <div className="flex gap-4 mt-2 text-xs">
                  <span className="text-green-700">{availableSlots.length} available</span>
                  {blockedTimeSlots.length > 0 && (
                    <span className="text-red-600">{blockedTimeSlots.length} blocked</span>
                  )}
                </div>
              </div>
            )}

            {checkingAvailability && (
              <div className="flex items-center gap-2 text-sm text-primary">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking availability...
              </div>
            )}

            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Player Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 relative">
                  <Label htmlFor="player_search" className="text-slate-700 font-medium">
                    Search Player
                  </Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      id="player_search"
                      type="text"
                      placeholder="Search by name or mobile"
                      value={playerSearch}
                      onChange={(e) => setPlayerSearch(e.target.value)}
                      className="pl-10"
                      data-testid="player-search-input"
                    />
                  </div>
                  {showSearchResults && searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      <div className="px-4 py-2 bg-slate-50 border-b border-slate-200">
                        <p className="text-xs text-slate-500 font-medium">{searchResults.length} player(s) found</p>
                      </div>
                      {searchResults.map((player, index) => (
                        <button
                          key={player.id || index}
                          type="button"
                          onClick={() => selectPlayer(player)}
                          className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b border-slate-100 last:border-b-0 transition-colors"
                          data-testid={`player-option-${index}`}
                        >
                          <p className="font-medium text-slate-900">{player.name}</p>
                          <p className="text-sm text-slate-600">{player.mobile !== 'N/A' ? player.mobile : 'No mobile'}</p>
                        </button>
                      ))}
                    </div>
                  )}
                  {showSearchResults && searchResults.length === 0 && playerSearch.length >= 2 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-4">
                      <p className="text-sm text-slate-500">No players found. You can add a new player below.</p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="player_name" className="text-slate-700 font-medium">
                    Player Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="player_name"
                    ref={playerNameRef}
                    type="text"
                    placeholder="Enter player name"
                    value={formData.player_name}
                    onChange={(e) => setFormData({ ...formData, player_name: e.target.value })}
                    required
                    data-testid="player-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="player_mobile" className="text-slate-700 font-medium">
                    Mobile Number
                  </Label>
                  <Input
                    id="player_mobile"
                    placeholder="Enter mobile number (optional)"
                    value={formData.player_mobile}
                    onChange={(e) => setFormData({ ...formData, player_mobile: e.target.value })}
                    data-testid="player-mobile-input"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-heading font-semibold text-lg text-slate-900 mb-4">Payment Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="amount" className="text-slate-700 font-medium">
                    Total Amount (₹) <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="Enter total amount"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    required
                    min="0"
                    step="0.01"
                    data-testid="amount-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="advance_payment" className="text-slate-700 font-medium">
                    Advance Payment (₹)
                  </Label>
                  <Input
                    id="advance_payment"
                    type="number"
                    placeholder="Enter advance payment"
                    value={formData.advance_payment}
                    onChange={(e) => setFormData({ ...formData, advance_payment: e.target.value })}
                    min="0"
                    step="0.01"
                    data-testid="advance-payment-input"
                  />
                </div>

                {/* Pending Amount Display */}
                {totalAmount > 0 && (
                  <div className="md:col-span-2">
                    <div className={`p-4 rounded-lg border ${pendingAmount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <IndianRupee className={`w-5 h-5 ${pendingAmount > 0 ? 'text-amber-600' : 'text-green-600'}`} />
                          <span className={`font-medium ${pendingAmount > 0 ? 'text-amber-800' : 'text-green-800'}`}>
                            {pendingAmount > 0 ? 'Pending to Collect at Venue' : 'Fully Paid'}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold text-2xl ${pendingAmount > 0 ? 'text-amber-700' : 'text-green-700'}`}>
                            ₹{pendingAmount.toLocaleString()}
                          </p>
                          {advancePayment > 0 && (
                            <p className="text-sm text-slate-600">
                              (Total: ₹{totalAmount.toLocaleString()} - Advance: ₹{advancePayment.toLocaleString()})
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="payment_mode" className="text-slate-700 font-medium">
                    Payment Mode <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.payment_mode}
                    onValueChange={(value) => setFormData({ ...formData, payment_mode: value })}
                    required
                  >
                    <SelectTrigger data-testid="payment-mode-select">
                      <SelectValue placeholder="Select payment mode" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_MODES.map((mode) => (
                        <SelectItem key={mode} value={mode}>
                          {mode}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="booking_source" className="text-slate-700 font-medium">
                    Booking Source <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.booking_source}
                    onValueChange={(value) => setFormData({ ...formData, booking_source: value })}
                    required
                  >
                    <SelectTrigger data-testid="booking-source-select">
                      <SelectValue placeholder="Select booking source" />
                    </SelectTrigger>
                    <SelectContent>
                      {BOOKING_SOURCES.map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="remarks" className="text-slate-700 font-medium">
                    Remarks (Optional)
                  </Label>
                  <Textarea
                    id="remarks"
                    placeholder="Add any additional notes"
                    value={formData.remarks}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    rows={3}
                    data-testid="remarks-textarea"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                type="submit"
                disabled={loading || checkingAvailability}
                className="bg-primary hover:bg-primary-hover transition-colors duration-200"
                data-testid="create-booking-button"
              >
                {loading ? 'Creating...' : 'Create Booking'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/office/bookings')}
                className="border-slate-200 hover:bg-slate-50"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </DashboardLayout>
  );
}